import { useEffect, useRef } from 'react';
import { getStoredToken } from '../services/tokenStore.js';

// SSE subscription to core's /events endpoint.
//
// Phase 3 scaffold: core/ does NOT expose /events yet. This hook is wired up
// so that pages can subscribe today; when the backend ships the endpoint,
// nothing on the frontend needs to change beyond confirming the event type
// strings (call_started, call_ended, transcript_appended).
//
// Behaviour today:
//   • If VITE_SSE_DISABLED is truthy, the hook is a no-op (useful in dev
//     while the endpoint 404s — keeps the console quiet).
//   • Otherwise it opens an EventSource at `<API_BASE>/events?token=…` and
//     dispatches each named event to the matching handler in `handlers`.
//   • On error it closes; React's effect cleanup re-opens on the next mount
//     so route changes naturally retry.
//
// EventSource cannot send Authorization headers, hence the query-string
// token. core/'s SSE handler must accept that form.
export function useEventStream(handlers = {}) {
  const handlersRef = useRef(handlers);
  handlersRef.current = handlers;

  useEffect(() => {
    if (import.meta.env.VITE_SSE_DISABLED) return undefined;

    const token = getStoredToken();
    if (!token) return undefined;

    const base = (import.meta.env.VITE_API_URL || '/api').replace(/\/+$/, '');
    let es;
    try {
      es = new EventSource(`${base}/events?token=${encodeURIComponent(token)}`, {
        withCredentials: true
      });
    } catch {
      return undefined;
    }

    const known = ['call_started', 'call_ended', 'transcript_appended'];
    const cleanup = [];

    for (const name of known) {
      const listener = (e) => {
        const handler = handlersRef.current?.[name];
        if (!handler) return;
        let payload = null;
        try {
          payload = e.data ? JSON.parse(e.data) : null;
        } catch {
          payload = e.data;
        }
        handler(payload, e);
      };
      es.addEventListener(name, listener);
      cleanup.push(() => es.removeEventListener(name, listener));
    }

    es.onerror = () => {
      // Don't spam reconnects — let the effect cleanup + remount handle it.
      es.close();
    };

    return () => {
      cleanup.forEach((fn) => fn());
      es.close();
    };
    // We intentionally don't depend on `handlers` (we read via ref) so callers
    // can pass inline objects without re-subscribing every render.
  }, []);
}
